import { useId } from 'react';
import { cn } from '@/shared/lib';
import type { SwitchProps } from './types';
import { descriptionStyles, labelStyles, switchStyles, thumbStyles, trackStyles } from './styles';

export type { SwitchProps } from './types';

/**
 * An on/off control.
 *
 * Built on a real `<button>` carrying `role="switch"` and `aria-checked`, with
 * `type="button"` forced. A switch is an immediate-action control, not a form
 * field, so it has no value to submit — and letting it default to `submit`
 * inside a form would fire that form on every toggle.
 *
 * The visible label lives inside the button and is referenced by
 * `aria-labelledby`, so the accessible name is the same text the user reads
 * rather than a second copy maintained only for assistive technology.
 */
export function Switch({
  label,
  description,
  checked,
  onCheckedChange,
  disabled = false,
  className,
  onClick,
  ...rest
}: SwitchProps) {
  const baseId = useId();
  const labelId = `${baseId}-label`;
  const descriptionId = `${baseId}-description`;

  return (
    <button
      {...rest}
      type="button"
      role="switch"
      aria-checked={checked}
      aria-labelledby={labelId}
      aria-describedby={description ? descriptionId : undefined}
      disabled={disabled}
      onClick={(event) => {
        onClick?.(event);
        if (!event.defaultPrevented) onCheckedChange?.(!checked);
      }}
      className={cn(switchStyles({ disabled }), className)}
    >
      <span aria-hidden="true" className={trackStyles({ checked })}>
        <span className={thumbStyles({ checked })} />
      </span>
      <span className="flex flex-col gap-0.5">
        <span id={labelId} className={labelStyles}>
          {label}
        </span>
        {description ? (
          <span id={descriptionId} className={descriptionStyles}>
            {description}
          </span>
        ) : null}
      </span>
    </button>
  );
}